/**
 * Turns any thrown thing into something an operator can act on.
 *
 * Written after a save that never left the browser was reported as "shows a
 * popup but nothing happens". The popup said "Could not save the settings" —
 * true, useless, and indistinguishable from a server rejection. The actual
 * cause was that the request never reached a server at all, which the message
 * could have said outright.
 *
 * Rules: never a bare generic string; always the server's own code and message
 * when there is one; and name the *class* of failure when there is not.
 */

import { ApiError } from './api';

export function describeError(err: unknown, fallback = 'The request failed'): string {
  if (err instanceof ApiError) {
    // `VALIDATION_ERROR: Request validation failed — brevo.senderEmail: Invalid email`
    const fields = err.details?.length
      ? ` — ${err.details.map((d) => `${d.field}: ${d.message}`).join('; ')}`
      : '';
    const requestId = err.requestId ? ` (request ${err.requestId})` : '';
    return `${err.code}: ${err.message}${fields}${requestId}`;
  }

  // A `TypeError: Failed to fetch` means the request never reached the API —
  // the dev server is down, the proxy is not running, or the network is gone.
  // Saying so is the difference between a five-minute fix and an evening.
  if (err instanceof TypeError || (err instanceof Error && /fetch|network/i.test(err.message))) {
    return `Could not reach the server — nothing was saved. Check that the API is running. (${err.message})`;
  }

  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
